import { getDbSafe } from './firebaseConfig';
import {
    collection,
    query,
    where,
    orderBy,
    limit,
    getDocs,
    getDoc,
    doc,
    Timestamp,
} from 'firebase/firestore';
import { UserService } from './api';
import { TagAffinityService } from './TagAffinityService';
import type { User, TopMatchResult } from '../types';

export type SwipeMode = 'classic' | 'matching';

/** User returned by fetchSimilarInterests — full user doc plus affinity metadata. */
export interface SimilarInterestUser extends User {
    affinityScore: number;
    overlappingTags: string[];
}

export interface FetchClassicPoolOptions {
    filters?: Record<string, any>;
    targetSize?: number;
    refreshKey?: number;
    /** Viewer's age bracket — forwarded to fetchDiscoverUsers for the Phase 2d list rule. */
    viewerAgeGroup?: 'adult' | 'minor';
}

export interface FetchSimilarInterestsOptions {
    limit?: number;
    minScore?: number;
    /** Age of the requesting user — used to enforce 18+/under-18 segregation. */
    viewerAge?: number;
}

/** Compute age from a DOB string (ISO "YYYY-MM-DD"). Returns null if DOB is absent or invalid.
 *  Callers must treat null as unknown age and fail closed (skip the candidate). */
function calcAge(dob?: string): number | null {
    if (!dob) return null;
    // Parse as local time — new Date('YYYY-MM-DD') is UTC midnight, which is the prior
    // calendar day in UTC- zones, causing an off-by-one at the 18th birthday boundary.
    const mm = dob.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
    if (!mm) return null;
    const b = new Date(parseInt(mm[1]), parseInt(mm[2]) - 1, parseInt(mm[3]));
    if (isNaN(b.getTime())) return null;
    const t = new Date();
    let a = t.getFullYear() - b.getFullYear();
    if (t.getMonth() < b.getMonth() || (t.getMonth() === b.getMonth() && t.getDate() < b.getDate())) a--;
    return a;
}

/** Returns true if viewer and candidate are on the same side of the 18 boundary. */
function sameAgeBracket(viewerAge: number, candidateAge: number): boolean {
    return (viewerAge >= 18) === (candidateAge >= 18);
}

/**
 * Tighter age-range gate used by the "Similar to Me" section:
 *   • Minors only ever see other minors (bracket segregation, fail-closed).
 *   • Adults 18–25 → candidate must be within ±5 years of the viewer.
 *   • Adults >25  → candidate must be within [viewerAge − 4, viewerAge + 5].
 * Adults never see minors regardless of the numeric range.
 */
function withinSimilarityAgeRange(viewerAge: number, candidateAge: number): boolean {
    if (viewerAge < 18) return candidateAge < 18;
    if (candidateAge < 18) return false;
    if (viewerAge <= 25) return Math.abs(candidateAge - viewerAge) <= 5;
    return candidateAge >= viewerAge - 4 && candidateAge <= viewerAge + 5;
}

export interface FetchNewYappersOptions {
    limit?: number;
    /** Age of the requesting user — used to enforce 18+/under-18 segregation. */
    viewerAge?: number;
}

export interface GroupUpdate {
    id: string;
    type: 'tribe' | 'forum';
    name: string;
    bannerUrl: string | null;
    tags: string[];
    /** Aggregated top member tags (nightly rollup) — lets general groups match a
     *  selected identity tag even without an explicit tag. */
    memberTags: string[];
    description: string;
    memberCount: number;
    latestPost: {
        content: string;
        author: string;
        username: string;
        avatar: string;
        timestamp: string;
        /** Author ids — required so viewers can filter out blocked authors.
         *  Without these the preview is just a string and blocking silently no-ops. */
        userId: string | null;
        authorId: string | null;
    } | null;
    lastActivityAt: number;
}

interface SessionCacheEntry {
    matchResults: TopMatchResult[];
    matchIds: Set<string>;
    computedAt: number;
}

// Module-level affinity pool cache.
// Stores up to 30 qualified matches per user and expires after 30 minutes.
// The SELECTION of which 10 to show is re-randomized on every call to
// fetchSimilarInterests — only the expensive cloud function call is cached.
const sessionCache = new Map<string, SessionCacheEntry>();
const POOL_TTL_MS = 30 * 60 * 1000; // 30 minutes

async function ensureSessionCache(
    userId: string,
    baseExcludeIds: string[]
): Promise<SessionCacheEntry> {
    const existing = sessionCache.get(userId);
    if (existing && (Date.now() - existing.computedAt) < POOL_TTL_MS) {
        console.log(`[DiscoverService] Pool cache hit (age: ${Math.round((Date.now() - existing.computedAt) / 1000)}s)`);
        return existing;
    }

    console.log('[DiscoverService] Pool cache miss/expired — calling getTopAffinityMatches');
    // minScore:2 casts a wider net; the 60%-of-smaller-pool rule is applied
    // client-side in fetchSimilarInterests after enriching user docs.
    const matchResults = await TagAffinityService.getTopMatches(userId, {
        limit: 50,
        minScore: 2,
        excludeIds: baseExcludeIds,
    });

    const entry: SessionCacheEntry = {
        matchResults,
        matchIds: new Set(matchResults.map(r => r.userId)),
        computedAt: Date.now(),
    };
    sessionCache.set(userId, entry);
    return entry;
}

export const DiscoverService = {

    /**
     * Clears the session cache for a specific user or all users.
     * Call on sign-out so the next Discover open recomputes fresh matches.
     */
    clearSessionCache(userId?: string): void {
        if (userId) {
            sessionCache.delete(userId);
        } else {
            sessionCache.clear();
        }
    },

    /**
     * Returns the classic swipe pool via the existing V1 tiered Firestore scan,
     * with Similar Interests users filtered out for mutual exclusion.
     *
     * Exclusion order applied:
     *   1. Caller-provided excludeIds (friends, blocked, swiped, passed/liked)
     *   2. Similar Interests match IDs (computed once per session, cached)
     *
     * If the user has fewer than 4 tags, TagAffinityService returns empty and
     * the classic pool is returned with no additional exclusions.
     *
     * @param userId      Requesting user's ID
     * @param excludeIds  Caller-built exclusion list (friends, blocked, swiped)
     * @param options     Forwarded to UserService.fetchDiscoverUsers
     */
    async fetchClassicPool(
        userId: string,
        excludeIds: string[],
        options: FetchClassicPoolOptions = {}
    ): Promise<User[]> {
        const { targetSize = 50, filters = {}, refreshKey = 0, viewerAgeGroup } = options;

        // The affinity lookup and the user scan run CONCURRENTLY.
        //
        // The affinity result is only used to keep this pool mutually exclusive
        // with the Similar Interests row — a DEDUPE. A dedupe does not have to be
        // known before the query is issued, only before the result is shown, so
        // awaiting it first put a whole Cloud Function round trip in front of the
        // scan for no reason. On a cold session cache that was the single largest
        // delay before the first card appeared.
        //
        // Over-fetching a little covers what the subtraction will remove, so the
        // deck is not left short when the two pools overlap.
        const poolPromise = UserService.fetchDiscoverUsers(
            userId,
            excludeIds,
            { ...filters, targetSize: targetSize + 10, refreshKey, viewerAgeGroup } as any
        );
        const cachePromise = ensureSessionCache(userId, excludeIds)
            .catch(e => {
                // Affinity is an optimisation. If it fails the deck should still
                // fill; the worst case is a face appearing in both places.
                console.warn('[DiscoverService] affinity lookup failed:', e);
                return { matchIds: new Set<string>() } as any;
            });

        const [pool, cache] = await Promise.all([poolPromise, cachePromise]);
        return pool
            .filter(u => !cache.matchIds.has(String(u.id)))
            .slice(0, targetSize);
    },

    /**
     * Returns the Similar Interests pool — users with ≥4 matching tags,
     * sorted by overlap score descending, capped at 10 results.
     *
     * Results are enriched with full user documents so V2 UI can render cards
     * without additional fetches. The session cache is shared with
     * fetchClassicPool, so whichever method is called first pays the Cloud
     * Function cost; the other call is free.
     *
     * @param userId      Requesting user's ID
     * @param excludeIds  Caller-built exclusion list (friends, blocked)
     */
    async fetchSimilarInterests(
        userId: string,
        excludeIds: string[],
        options: FetchSimilarInterestsOptions = {}
    ): Promise<SimilarInterestUser[]> {
        const { viewerAge } = options;
        const cache = await ensureSessionCache(userId, excludeIds);

        if (cache.matchResults.length === 0) {
            return [];
        }

        const db = await getDbSafe();

        // Fetch the viewer's own tag count to compute the 60%-of-smaller-pool threshold.
        let viewerTagCount = 0;
        try {
            const viewerSnap = await getDoc(doc(db, 'users', userId));
            if (viewerSnap.exists()) {
                const vd = viewerSnap.data();
                viewerTagCount = [...(vd.identities ?? []), ...(vd.interests ?? [])].length;
            }
        } catch { /* default to 0 — threshold falls back to minScore:2 */ }

        const allExcluded = new Set([userId, ...excludeIds]);
        const enriched: SimilarInterestUser[] = [];

        await Promise.all(
            cache.matchResults.map(async (match) => {
                if (allExcluded.has(match.userId)) return;

                try {
                    const snap = await getDoc(doc(db, 'users', match.userId));
                    if (!snap.exists()) return;

                    const userData = { id: snap.id, ...snap.data() } as User;

                    if (userData.showInDiscover === false) return;
                    // Moderation: hidden accounts are invisible in discovery.
                    if ((userData as any).accountHidden === true) return;

                    // Standard 18-bracket segregation only — narrower per-user
                    // age filtering is applied client-side via the Discover
                    // filter modal's ageRange, so it can be tuned without a
                    // re-fetch.
                    {
                        const effectiveViewerAge = viewerAge ?? 0; // 0 < 18 → minor (fail-closed)
                        const candidateAge = calcAge(userData.dateOfBirth);
                        if (candidateAge === null || !sameAgeBracket(effectiveViewerAge, candidateAge)) return;
                    }

                    // Smooth scaling threshold — Option C.
                    // factor = 0.20 + 0.05 × smallerPool (a gentle quadratic ramp).
                    // "Smaller" = min(viewer tag count, candidate tag count).
                    //
                    // Required overlaps vs the old flat 60% rule:
                    //   smaller=4 → factor=0.40 → required=2  (was 3 — the main fix)
                    //   smaller=5 → factor=0.45 → required=3  (same)
                    //   smaller=8 → factor=0.60 → required=5  (same)
                    //   smaller=10→ factor=0.70 → required=7  (stricter than 60%'s 6)
                    //
                    // Effect: users with few tags (≤4) can match on 2 shared tags
                    // instead of needing 3. Users with many tags face a rising bar
                    // that exceeds the old 60% at 9+ tags — maintaining signal quality.
                    const candidateTagCount = [
                        ...(userData.identities ?? []),
                        ...(userData.interests ?? []),
                    ].length;
                    const smallerPool      = Math.min(viewerTagCount, candidateTagCount);
                    const smoothFactor     = 0.20 + 0.05 * smallerPool;
                    const requiredOverlap  = Math.max(2, Math.ceil(smallerPool * smoothFactor));
                    if (match.score < requiredOverlap) return;

                    enriched.push({
                        ...userData,
                        affinityScore: match.score,
                        overlappingTags: match.overlappingTags,
                    });
                } catch {
                    // Skip users whose documents fail to load rather than
                    // surfacing a partial failure to the caller.
                }
            })
        );

        // Shuffle the enriched pool so every screen visit shows a different
        // random 10 from the 30-candidate affinity pool (Fisher-Yates).
        for (let i = enriched.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [enriched[i], enriched[j]] = [enriched[j], enriched[i]];
        }
        return enriched.slice(0, 10);
    },

    /**
     * Returns the full Similar Interests candidate pool IDs for this user's
     * session — used by callers to exclude these users from other sections.
     * Returns an empty Set if the cache hasn't been populated yet.
     */
    getSimilarInterestPoolIds(userId: string): Set<string> {
        return sessionCache.get(userId)?.matchIds ?? new Set();
    },

    /**
     * Returns up to 10 users created in the last 30 days, randomly selected.
     * Users who appear in the Similar Interests pool are excluded at the
     * caller level via getSimilarInterestPoolIds.
     *
     * Required Firestore composite index on the `users` collection:
     *   showInDiscover ASC, createdAt DESC
     *
     * @param userId      Requesting user's ID (excluded from results)
     * @param excludeIds  Caller-provided list: friends + blocked + Similar Interests pool
     * @param options.limit  Max results to return (default 10)
     */
    async fetchNewYappersThisMonth(
        userId: string,
        excludeIds: string[],
        options: FetchNewYappersOptions = {}
    ): Promise<User[]> {
        const { limit: resultLimit = 10, viewerAge } = options;
        // Bracket derived from viewerAge — fail-closed: missing/minor → 'minor'.
        const viewerAgeGroup: 'adult' | 'minor' = (viewerAge !== undefined && viewerAge >= 18) ? 'adult' : 'minor';

        const db = await getDbSafe();
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

        // ageGroup filter required by Phase 2d list rule.
        // Uses composite index: (showInDiscover, ageGroup, createdAt DESC).
        const q = query(
            collection(db, 'users'),
            where('showInDiscover', '==', true),
            where('ageGroup', '==', viewerAgeGroup),
            where('createdAt', '>=', Timestamp.fromDate(thirtyDaysAgo)),
            orderBy('createdAt', 'desc'),
            limit(500)
        );

        const snap = await getDocs(q);
        const allExcluded = new Set([userId, ...excludeIds]);

        // Collect all valid candidates, applying age segregation if viewerAge is known.
        const candidates: User[] = [];
        for (const docSnap of snap.docs) {
            if (allExcluded.has(docSnap.id)) continue;
            const data = docSnap.data();
            // Moderation: hidden accounts are invisible in discovery.
            if ((data as any).accountHidden === true) continue;
            // A permanently banned account is off the app entirely — their posts
            // are hidden by onUserBanStateChanged, so surfacing the person here
            // would offer a profile with nothing behind it. Temporary bans are a
            // cooling-off period and stay discoverable.
            {
                const d = data as any;
                const permanentlyBanned = (d.banned === true || d.status === 'banned')
                    && (d.banExpiry === null || d.banExpiry === undefined);
                if (permanentlyBanned) continue;
            }
            // Age segregation: 18+ viewers never see minors, minors never see 18+.
            // Fail-closed on both sides: unknown viewer age (no DOB) → minor; unknown candidate → skip.
            {
                const effectiveViewerAge = viewerAge ?? 0;
                const candidateAge = calcAge(data.dateOfBirth);
                if (candidateAge === null || !sameAgeBracket(effectiveViewerAge, candidateAge)) continue;
            }
            candidates.push({ id: docSnap.id, ...data } as User);
        }

        // Fisher-Yates shuffle so every screen visit shows a different random 10.
        for (let i = candidates.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [candidates[i], candidates[j]] = [candidates[j], candidates[i]];
        }

        return candidates.slice(0, resultLimit);
    },

    /**
     * Fetches the most recently active tribes and forums for the Group Updates
     * section in Discover. Returns up to `resultLimit` groups sorted by
     * lastActivityAt descending, each with their latest post attached.
     */
    async fetchGroupUpdates(
        resultLimit = 8,
        /** Optional viewer-scoped filter (muted words). Applied BEFORE the result
         *  is trimmed to `resultLimit`, so hidden groups are replaced rather than
         *  simply leaving the section short. Services stay pure: the caller
         *  supplies the predicate from useContentFilter. */
        isMutedGroup?: (group: any) => boolean,
    ): Promise<GroupUpdate[]> {
        const db = await getDbSafe();

        // Over-fetch relative to resultLimit so viewer-side filtering has spares
        // to draw on. These were 6 + 6 against a limit of 8, leaving no headroom.
        const tribesQ = query(
            collection(db, 'tribes'),
            orderBy('lastActivityAt', 'desc'),
            limit(12)
        );
        const forumsQ = query(
            collection(db, 'forums'),
            orderBy('lastActivityAt', 'desc'),
            limit(12)
        );

        const [tribesSnap, forumsSnap] = await Promise.all([
            getDocs(tribesQ),
            getDocs(forumsQ),
        ]);

        // Build a lightweight group record for each result.
        const groups: Array<Omit<GroupUpdate, 'latestPost'> & { postCollection: string; groupField: string }> = [];

        tribesSnap.docs.forEach(d => {
            const data = d.data();
            if (data.status === 'Archived') return;
            groups.push({
                id: d.id,
                type: 'tribe',
                name: data.name ?? '',
                bannerUrl: data.bannerUrl ?? data.bannerImage ?? null,
                tags: data.tags ?? data.identities ?? [],
                memberTags: data.memberTags ?? [],
                description: data.description ?? '',
                memberCount: data.memberCount ?? 0,
                lastActivityAt: data.lastActivityAt?.toMillis?.() ?? 0,
                postCollection: 'tribe_posts',
                groupField: 'tribeId',
            });
        });

        forumsSnap.docs.forEach(d => {
            const data = d.data();
            if (data.status === 'Archived') return;
            groups.push({
                id: d.id,
                type: 'forum',
                name: data.name ?? '',
                bannerUrl: data.bannerUrl ?? data.bannerImage ?? null,
                tags: data.tags ?? data.identities ?? [],
                memberTags: data.memberTags ?? [],
                description: data.description ?? '',
                memberCount: data.memberCount ?? 0,
                lastActivityAt: data.lastActivityAt?.toMillis?.() ?? 0,
                postCollection: 'forum_posts',
                groupField: 'topicId',
            });
        });

        // Sort combined list and take top N before doing post lookups.
        groups.sort((a, b) => b.lastActivityAt - a.lastActivityAt);
        // Filter before trimming, so a muted group is backfilled by the next
        // eligible one instead of leaving a gap in the section.
        const eligible = isMutedGroup ? groups.filter(g => !isMutedGroup(g)) : groups;
        const top = eligible.slice(0, resultLimit);

        /**
         * Newest surviving post from one collection for one group.
         *
         * The ordered query is the point: this previously read five documents
         * with NO orderBy and sorted those. Firestore returns an unordered
         * query in document-id order, which is unrelated to time, so for any
         * group with more than five posts the "latest post" was the newest of
         * five arbitrary ones — and since the section's final ordering keys off
         * that timestamp, the whole row ranked by a near-random date.
         *
         * limit(5) rather than limit(1) so a deleted or hidden newest post
         * falls through to the one behind it.
         */
        const newestPost = async (collectionName: string, field: string, groupId: string): Promise<any | null> => {
            const pick = (docs: any[]) => {
                const alive = docs
                    .map(d => d.data())
                    .filter((p: any) => !p.deleted && !p.is_hidden)
                    .sort((a: any, b: any) => (b.timestamp?.toMillis?.() ?? 0) - (a.timestamp?.toMillis?.() ?? 0));
                return alive.length > 0 ? alive[0] : null;
            };

            try {
                const snap = await getDocs(query(
                    collection(db, collectionName),
                    where(field, '==', groupId),
                    orderBy('timestamp', 'desc'),
                    limit(5),
                ));
                return pick(snap.docs);
            } catch {
                // The composite index this needs may not be deployed yet (see
                // firestore.indexes.json). Degrade to a wider unordered scan
                // rather than showing nothing: still approximate, but 40 docs
                // is far likelier to contain the newest than 5 were.
                try {
                    const snap = await getDocs(query(
                        collection(db, collectionName),
                        where(field, '==', groupId),
                        limit(40),
                    ));
                    return pick(snap.docs);
                } catch {
                    return null;
                }
            }
        };

        const withPosts: GroupUpdate[] = await Promise.all(
            top.map(async g => {
                // A forum's activity has two homes, and the forum screen already
                // merges both: posts written inside the forum (forum_posts.topicId)
                // and YapTab yaps tagged with a forum topic (yaps.forumTopic.forumId).
                // Reading only the first made a forum whose recent activity is
                // cross-posts look dormant here, even though createYap bumps its
                // lastActivityAt for exactly those posts.
                const sources: Array<[string, string]> = g.type === 'forum'
                    ? [['forum_posts', 'topicId'], ['yaps', 'forumTopic.forumId']]
                    : [['tribe_posts', 'tribeId']];

                const candidates = (await Promise.all(
                    sources.map(([coll, field]) => newestPost(coll, field, g.id))
                )).filter(Boolean);

                candidates.sort((a: any, b: any) => (b.timestamp?.toMillis?.() ?? 0) - (a.timestamp?.toMillis?.() ?? 0));
                const p: any = candidates[0] || null;

                const latestPost: GroupUpdate['latestPost'] = p ? {
                    content: p.content ?? '',
                    author: p.author ?? 'Someone',
                    username: (p.username ?? '').replace(/^@/, ''),
                    avatar: p.avatar ?? '',
                    timestamp: p.timestamp?.toDate?.().toISOString() ?? new Date().toISOString(),
                    // Carried so the caller can drop previews authored by a
                    // blocked user. `authorId` covers anonymous posts, where
                    // `userId` is not the real author.
                    userId: p.userId != null ? String(p.userId) : null,
                    authorId: p.authorId != null ? String(p.authorId) : null,
                } : null;

                const { postCollection: _pc, groupField: _gf, ...rest } = g;
                return { ...rest, latestPost };
            })
        );

        // Most recently active first. Falling back to the group's own
        // lastActivityAt matters: a group whose post lookup came back empty
        // (posts all deleted, a read that failed) used to sort as though it had
        // never been active at all and sank below genuinely older groups.
        const recencyOf = (g: GroupUpdate) => Math.max(
            g.latestPost ? new Date(g.latestPost.timestamp).getTime() : 0,
            g.lastActivityAt || 0,
        );
        withPosts.sort((a, b) => recencyOf(b) - recencyOf(a));

        return withPosts;
    },
};
